import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, Card, Row, Col, Form, Button, Alert, 
  Badge, ProgressBar, InputGroup, Tab, Tabs
} from 'react-bootstrap';
import { 
  Palette, Upload, Eye, RefreshCw, Save, 
  Image, Settings, Paintbrush, Check, X 
} from 'lucide-react';
import { boutiqueService } from '../../services/boutiqueService';
import { useTheme } from '../../contexts/ThemeContext';
import BoutiqueLogo, { BoutiqueLogoLarge } from './BoutiqueLogo'; // ✅ Import du composant logo

const BoutiqueConfigModal = ({ show, onHide, boutique, onSave }) => {
  const { applyBoutiqueTheme } = useTheme();
  const fileInputRef = useRef(null);

  // États
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('colors');

  // Configuration
  const [config, setConfig] = useState({
    logoUrl: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#1d4ed8',
    previewMode: false
  });

  // Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Charger la configuration au montage
  useEffect(() => {
    if (show && boutique) {
      loadBoutiqueConfig();
    }
  }, [show, boutique]);

  const loadBoutiqueConfig = async () => {
    setLoading(true);
    try {
      const response = await boutiqueService.getBoutiqueConfig(boutique.id);
      
      if (response.success) {
        setConfig({
          logoUrl: response.data.logoUrl || boutique.logoUrl || '',
          primaryColor: response.data.primaryColor || boutique.primaryColor || '#3b82f6',
          secondaryColor: response.data.secondaryColor || boutique.secondaryColor || '#1d4ed8',
          previewMode: false
        });
      } else {
        // Fallback avec les données de la boutique
        setConfig({
          logoUrl: boutique.logoUrl || '',
          primaryColor: boutique.primaryColor || '#3b82f6',
          secondaryColor: boutique.secondaryColor || '#1d4ed8',
          previewMode: false
        });
        setErrors({ load: response.message });
      }
    } catch (error) {
      // Fallback avec les données de la boutique
      setConfig({
        logoUrl: boutique.logoUrl || '',
        primaryColor: boutique.primaryColor || '#3b82f6',
        secondaryColor: boutique.secondaryColor || '#1d4ed8',
        previewMode: false
      });
      setErrors({ load: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (type, color) => {
    setConfig(prev => ({
      ...prev,
      [type]: color
    }));
    
    // Appliquer la prévisualisation
    if (config.previewMode) {
      const newBoutique = {
        ...boutique,
        [type]: color
      };
      applyBoutiqueTheme?.(newBoutique);
    }
  };

  const togglePreview = () => {
    const newPreviewMode = !config.previewMode;
    setConfig(prev => ({ ...prev, previewMode: newPreviewMode }));
    
    if (newPreviewMode) {
      // Activer la prévisualisation
      const previewBoutique = {
        ...boutique,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor
      };
      applyBoutiqueTheme?.(previewBoutique);
    } else {
      // Revenir aux couleurs originales
      applyBoutiqueTheme?.(boutique);
    }
  };

  const handleClose = () => {
    // Restaurer les couleurs originales
    if (config.previewMode) {
      applyBoutiqueTheme?.(boutique);
    }
    
    // Reset des états
    setConfig({
      logoUrl: '',
      primaryColor: '#3b82f6',
      secondaryColor: '#1d4ed8',
      previewMode: false
    });
    setErrors({});
    setSuccess('');
    setLogoFile(null);
    setLogoPreview(null);
    setUploadProgress(0);
    
    onHide();
  };

  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation du fichier
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors({ logo: 'Format de fichier non supporté. Utilisez JPG, PNG ou WebP' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setErrors({ logo: 'Fichier trop volumineux (5MB maximum)' });
      return;
    }

    setLogoFile(file);
    setErrors(prev => ({ ...prev, logo: '' }));

    // Créer la prévisualisation
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;

    try {
      setUploadProgress(0);
      
      const response = await boutiqueService.uploadLogo(
        boutique.id, 
        logoFile, 
        (progress) => setUploadProgress(progress)
      );

      if (response.success) {
        setConfig(prev => ({ ...prev, logoUrl: response.data.logoUrl }));
        setLogoFile(null);
        setLogoPreview(null);
        return response.data.logoUrl;
      } else {
        setErrors({ logo: response.message });
        return null;
      }
    } catch (error) {
      setErrors({ logo: 'Erreur lors de l\'upload du logo' });
      return null;
    }
  };

  const saveColors = async () => {
    try {
      const response = await boutiqueService.updateBoutiqueColors(boutique.id, {
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor
      });

      if (response.success) {
        return true;
      } else {
        setErrors({ colors: response.message });
        return false;
      }
    } catch (error) {
      setErrors({ colors: 'Erreur lors de la mise à jour des couleurs' });
      return false;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    setSuccess('');

    try {
      let logoUrl = config.logoUrl;

      // Upload du logo si nécessaire
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl) {
          setSaving(false);
          return;
        }
      }

      // Sauvegarde des couleurs
      const colorsSuccess = await saveColors();
      if (!colorsSuccess) {
        setSaving(false);
        return;
      }

      // Appliquer le nouveau thème
      const updatedBoutique = {
        ...boutique,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        logoUrl: logoUrl
      };
      
      applyBoutiqueTheme?.(updatedBoutique);
      setConfig(prev => ({ ...prev, previewMode: false }));

      setSuccess('Configuration mise à jour avec succès !');
      
      // Appeler le callback parent
      if (onSave) {
        onSave(updatedBoutique);
      }

    } catch (error) {
      setErrors({ save: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <Settings className="me-2" />
          ⚙️ Configuration - {boutique?.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center p-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Alertes */}
            {errors.load && (
              <Alert variant="warning" className="mb-3">
                {errors.load}
              </Alert>
            )}
            
            {success && (
              <Alert variant="success" className="mb-3">
                {success}
              </Alert>
            )}

            {/* ✅ Aperçu de la boutique avec logo */}
            <Card className="mb-4" style={{ 
              background: `linear-gradient(135deg, ${config.primaryColor}, ${config.secondaryColor})`,
              color: 'white'
            }}>
              <Card.Body className="text-center">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  {/* ✅ Affichage du logo actuel avec prévisualisation */}
                  <div className="me-3">
                    {logoPreview ? (
                      <img 
                        src={logoPreview}
                        alt="Aperçu logo"
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: '3px solid rgba(255, 255, 255, 0.3)'
                        }}
                      />
                    ) : (
                      <BoutiqueLogoLarge 
                        boutique={{
                          ...boutique,
                          logoUrl: config.logoUrl,
                          primaryColor: config.primaryColor,
                          secondaryColor: config.secondaryColor
                        }}
                        showPlaceholder={true}
                        style={{
                          border: '3px solid rgba(255, 255, 255, 0.3)'
                        }}
                      />
                    )}
                  </div>
                  
                  <div>
                    <h4 className="mb-1">{boutique?.name}</h4>
                    <Badge bg="light" text="dark">Aperçu de la configuration</Badge>
                  </div>
                </div>
                
                {config.previewMode && (
                  <Badge bg="warning" className="mt-2">
                    Mode prévisualisation actif
                  </Badge>
                )}
              </Card.Body>
            </Card>

            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
              
              {/* ✅ Onglet Couleurs */}
              <Tab eventKey="colors" title={
                <span><Palette className="me-2" size={16} />Couleurs</span>
              }>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Couleur Primaire</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="color"
                              value={config.primaryColor}
                              onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                              className="me-3"
                              style={{ width: '60px', height: '40px' }}
                            />
                            <Form.Control
                              type="text"
                              value={config.primaryColor}
                              onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                              placeholder="#3b82f6"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Couleur Secondaire</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="color"
                              value={config.secondaryColor}
                              onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                              className="me-3"
                              style={{ width: '60px', height: '40px' }}
                            />
                            <Form.Control
                              type="text"
                              value={config.secondaryColor}
                              onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                              placeholder="#1d4ed8"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between">
                      <Button
                        variant={config.previewMode ? 'warning' : 'outline-primary'}
                        onClick={togglePreview}
                      >
                        <Eye size={16} className="me-2" />
                        {config.previewMode ? 'Arrêter la prévisualisation' : 'Prévisualiser'}
                      </Button>
                    </div>

                    {errors.colors && (
                      <Alert variant="danger" className="mt-3">
                        {errors.colors}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* ✅ Onglet Logo */}
              <Tab eventKey="logo" title={
                <span><Image className="me-2" size={16} />Logo</span>
              }>
                <Card>
                  <Card.Body>
                    {/* Logo actuel */}
                    {(config.logoUrl || logoPreview) && (
                      <div className="text-center mb-4">
                        <h6>Logo actuel</h6>
                        <div className="border rounded p-3 bg-light d-inline-block">
                          {logoPreview ? (
                            <img
                              src={logoPreview}
                              alt="Aperçu logo"
                              style={{ maxWidth: '200px', maxHeight: '100px' }}
                              className="img-fluid"
                            />
                          ) : (
                            <BoutiqueLogo 
                              boutique={{
                                ...boutique,
                                logoUrl: config.logoUrl
                              }}
                              size="xl"
                              showPlaceholder={true}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Upload */}
                    <div className="text-center">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoSelect}
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        style={{ display: 'none' }}
                      />
                      
                      <Button
                        variant="outline-primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={saving}
                      >
                        <Upload size={16} className="me-2" />
                        {config.logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                      </Button>
                      
                      <div className="mt-2 text-muted small">
                        Formats acceptés: JPG, PNG, WebP (5MB max)
                      </div>
                    </div>

                    {/* Progression upload */}
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-3">
                        <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
                      </div>
                    )}

                    {/* Fichier sélectionné */}
                    {logoFile && (
                      <Alert variant="info" className="mt-3">
                        <strong>Fichier sélectionné:</strong> {logoFile.name}
                        <br />
                        <small>Le logo sera uploadé lors de la sauvegarde</small>
                      </Alert>
                    )}

                    {errors.logo && (
                      <Alert variant="danger" className="mt-3">
                        {errors.logo}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

            </Tabs>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {config.previewMode && (
            <Badge bg="warning">
              Mode prévisualisation actif
            </Badge>
          )}
        </div>
        
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Sauvegarde...</span>
                </div>
                Sauvegarde...
              </>
            ) : (
              <>
                <Save size={16} className="me-2" />
                Sauvegarder
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>

      {/* ✅ Styles CSS pour le modal */}
      <style jsx>{`
        .color-preview:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </Modal>
  );
};

export default BoutiqueConfigModal;